/**
 * balance-emp.ts — EMP balance constants (v3M4, MECHANICS §2.4).
 *
 * Co-locates the per-tick disable count, the per-Mk Electromagnetic
 * Protection reduction ladder, and the default blast radius. Tuning
 * is a v3M10 balance-pass concern; these starter values match the
 * MECHANICS §2.4 lock from 2026-04-21.
 */

import type { Empire, GameState, ShipTemplate, BalanceData } from '../state/types';
import { highestParsedValue } from './research-effects';

export function empProtectionReductionByMk(balance: BalanceData) {
  return balance.emp.protectionReductionByMk;
}

/**
 * Default sim-coord blast radius for an EMP weapon when its `weapon.blastRadius`
 * is null in the data. v1 EMP weapon ships with `null`; this default makes
 * the v3M4 fire path produce a meaningful effect without requiring a data
 * round-trip. Same magnitude as the Concussion Torpedo's authored 300 →
 * 300 × WEAPON_RANGE_SCALE (0.0005) = 0.15 sim-units.
 *
 * v3M10.1: migrated to state.balanceData.emp.defaultBlastRadius.
 */

/**
 * Effective EMP Protection (in disable-ticks reduction) for a ship of
 * `tmpl` chassis owned by `empire`. Sums:
 *   1. Per-Mk reduction from state.balanceData.emp.protectionReductionByMk, indexed
 *      by the empire's highest Electromagnetic Protection Mk research.
 *   2. The chassis's authored `empProtection` stat.
 *
 * Result is a positive number of ticks to subtract from the base disable.
 * Caller (`advance-combat.ts`) clamps the final disable to `max(0, base − this)`.
 */
export function effectiveEmpProtection(
  state: GameState,
  empire: Empire,
  tmpl: ShipTemplate,
): number {
  // MFix7.2 — read the tick-reduction from the parsed effect string
  // on the highest-researched EMP Protection tech. Falls back to the
  // legacy table on `state.balanceData` when no researched tech in
  // that category carries a parsed value (e.g. older tech-tree.json
  // pre-MFix7.2 regen, or test fixtures with empty researchedTechs).
  const fromTech = highestParsedValue(
    state,
    empire,
    'Electromagnetic Protection',
    'parsedAdditive',
    NaN,
  );
  let mkReduction: number;
  if (Number.isFinite(fromTech)) {
    mkReduction = fromTech;
  } else {
    // Determine legacy Mk inline since the dedicated empProtectionMk
    // export was retired alongside the other Mk accessors.
    let bestMk = 1;
    for (const techName of empire.researchedTechs) {
      const tech = state.techs.get(techName);
      if (!tech || tech.category !== 'Electromagnetic Protection') continue;
      if (tech.level !== null && tech.level > bestMk) bestMk = tech.level;
    }
    const reductionTable = empProtectionReductionByMk(state.balanceData);
    mkReduction = reductionTable[Math.max(0, Math.min(5, bestMk - 1))] ?? 0;
  }
  return mkReduction + tmpl.empProtection;
}

/**
 * Final disable-tick count to apply to a target ship caught in an EMP
 * blast. Combines state.balanceData.emp.baseDisableTicks with the target's
 * effective EMP Protection, floored at 0 (a heavily-protected ship can
 * shrug off the blast entirely).
 */
export function empDisableTicksForTarget(
  state: GameState,
  empire: Empire,
  tmpl: ShipTemplate,
): number {
  const reduction = effectiveEmpProtection(state, empire, tmpl);
  return Math.max(0, state.balanceData.emp.baseDisableTicks - reduction);
}
